/**
 * Main Footer Component
 *
 * The primary footer for the American Police Training platform. Displays
 * company information, links, contact details, and legal information.
 * Provides consistent footer layout across all pages.
 *
 * @author Andres Nava
 * @version 1.0.0
 */
import React from 'react';
import { isViteFlagEnabled } from '../../utils/runtimePlatformFlags';
import { isPerDiemEstimatorEnabled } from '../../utils/perDiemFeature';
// import { logger } from "../../utils/logger";
import { Box, Typography, Link, Grid, Divider, useTheme, useMediaQuery, Accordion, AccordionSummary, AccordionDetails, } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { AGENCY_SHELL_HORIZONTAL_PX } from '../../constants/layoutSpacing';
import { Facebook, Twitter, LinkedIn, Email, Phone, LocationOn, ExpandMore as ExpandMoreIcon, } from '@mui/icons-material';
import ResponsiveLogo from '../common/ResponsiveLogo';
import VisuallyHiddenText from '../common/VisuallyHiddenText';
import SecurityStatusWidget from '../security/SecurityStatusWidget';
import { useFooterSettings } from '../../hooks/useFooterSettings';
import { getBlogCanonicalBaseUrl, getNavigationUrl, getAuthUrl } from '../../utils/subdomainUtils';
import { cachedFetch } from '../../utils/footerCache';
import { logger } from "../../utils/logger";
import { useSiteContact } from '../../hooks/useSiteContact';
import { resolvePublicMainPhone } from '../../../shared/siteContact/resolvePublicMainPhone';
import { resolvePublicMainAddress } from '../../../shared/siteContact/resolvePublicMainAddress';
import { isLmsAppShellPath } from '../../../shared/layout/lmsAppShellPaths';
import { isScorecardReadinessPublicPath } from '../../../shared/scorecard/readinessScorecardConfig';
import { TICS_TRADEMARK_ATTRIBUTION } from '../../../shared/tics/public/ticsBranding';
// TypeScript interfaces for better type safety
interface FooterLink {
    text: string;
    href: string;
    onClick?: () => void;
}
interface FooterSection {
    title: string;
    links: FooterLink[];
}
interface SocialLink {
    icon: React.ReactElement;
    href: string;
    label: string;
}
interface CompanyContactsResponse {
    success: boolean;
    data?: {
        'general:phone'?: Array<{
            value: string;
        }>;
        'general:email'?: Array<{
            value: string;
        }>;
        'general:address'?: Array<{
            value: string;
        }>;
    };
}
// Paths disallowed in robots.txt - add nofollow so crawlers are not encouraged to request them
function isRobotsDisallowedPath(href: string): boolean {
    try {
        const url = new URL(href, 'https://americanpolicetraining.com');
        const path = url.pathname.toLowerCase();
        const host = url.hostname.toLowerCase();
        return path.startsWith('/api/') || path.startsWith('/admin') || path.startsWith('/dashboard') || path.startsWith('/backoffice')
            || host.includes('dash.') || host.includes('backoffice.');
    } catch {
        return false;
    }
}

const Footer: React.FC = () => {
    const theme = useTheme();
    const location = useLocation();
    const currentYear = new Date().getFullYear();
    const { settings, loading } = useFooterSettings();
    const { data: siteContact } = useSiteContact();
    const [primaryPhone, setPrimaryPhone] = React.useState<string | null>(null);
    const [primaryEmail, setPrimaryEmail] = React.useState<string | null>(null);
    const [primaryAddress, setPrimaryAddress] = React.useState<string | null>(null);
    // Responsive breakpoints
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const appShellFooter = isLmsAppShellPath(location.pathname);
    const scorecardFooter = isScorecardReadinessPublicPath(location.pathname);
    /** Settings / people-profile redirect — denser footer so the form stays primary. */
    const denseSettingsFooter =
        location.pathname === '/dashboard/settings' ||
        location.pathname === '/dashboard/people-profile' ||
        location.pathname.startsWith('/dashboard/settings/');
    React.useEffect(() => {
        let isMounted = true;
        const loadPrimary = async () => {
            try {
                const json = await cachedFetch('/api/company-contacts/public/primary') as CompanyContactsResponse;
                if (isMounted) {
                    if (json?.success && json.data) {
                        setPrimaryPhone(json.data['general:phone']?.[0]?.value || null);
                        setPrimaryEmail(json.data['general:email']?.[0]?.value || null);
                        setPrimaryAddress(json.data['general:address']?.[0]?.value || null);
                    }
                    else {
                        // API returned success: false or no data
                        logger.api('Footer: Company contacts API returned no data', "GET");
                    }
                }
            }
            catch (error) {
                // Network error or other exception
                if (isMounted) {
                    logger.error('Footer: Could not load company contacts', error, {});
                }
            }
        };
        // Use setTimeout to make this non-blocking
        const timeoutId = setTimeout(loadPrimary, 100);
        return () => {
            isMounted = false;
            clearTimeout(timeoutId);
        };
    }, []);
    // Memoize footer sections to prevent unnecessary re-renders
    const footerSections = React.useMemo((): FooterSection[] => [
        {
            title: 'Company',
            links: [
                { text: 'Home', href: getNavigationUrl('/') },
                { text: 'About Us', href: getNavigationUrl('/about') },
                { text: 'Contact', href: getNavigationUrl('/contact') },
                { text: 'Careers', href: getNavigationUrl('/careers') },
            ],
        },
        {
            title: 'Services',
            links: [
                { text: 'Training Events', href: getNavigationUrl('/events') },
                { text: 'Venues', href: getNavigationUrl('/venues') },
                { text: 'Online Courses', href: getNavigationUrl('/courses') },
                ...(isViteFlagEnabled(
                    'VITE_TICS_CREDENTIAL_CATALOG_ENABLED',
                    import.meta.env.VITE_TICS_CREDENTIAL_CATALOG_ENABLED,
                )
                    ? [{ text: 'Credentials', href: getNavigationUrl('/credentials') }]
                    : []),
                { text: 'Certification', href: getNavigationUrl('/certification') },
                ...(isViteFlagEnabled('VITE_VENDOR_DIRECTORY_ENABLED', import.meta.env.VITE_VENDOR_DIRECTORY_ENABLED)
                    ? [
                        { text: 'Vendor Directory', href: getNavigationUrl('/vendors') },
                        { text: 'List your company', href: getNavigationUrl('/vendors/join') },
                      ]
                    : []),
                { text: 'Secondary Employment Listing', href: getNavigationUrl('/secondary-employment/for-employers') },
            ],
        },
        {
            title: 'Legal',
            links: [
                { text: 'Terms of Service', href: getNavigationUrl('/terms') },
                { text: 'Privacy Policy', href: getNavigationUrl('/privacy') },
                { text: 'Security', href: getNavigationUrl('/security') },
                { text: 'Cookie Policy', href: getNavigationUrl('/cookie-policy') },
                { text: 'Cookie Settings', href: '#', onClick: () => window.dispatchEvent(new CustomEvent('openCookieSettings')) },
                { text: 'Data Request', href: getNavigationUrl('/data-request') },
                { text: 'GDPR Information', href: getNavigationUrl('/gdpr-compliance') },
            ],
        },
        {
            title: 'Account & Preferences',
            links: [
                { text: 'Login', href: getAuthUrl('/login', { includeRedirect: false }) },
                { text: 'Sign Up', href: getAuthUrl('/signup', { includeRedirect: false }) },
                { text: 'Forgot Password', href: getAuthUrl('/forgot-password', { includeRedirect: false }) },
                { text: 'Dashboard', href: getNavigationUrl('/dashboard') },
                { text: 'User Preferences', href: getNavigationUrl('/preferences') },
                { text: 'Analytics Dashboard', href: getNavigationUrl('/analytics') },
            ],
        },
        {
            title: 'Resources',
            links: [
                { text: 'Knowledge Base', href: getNavigationUrl('/knowledge') },
                { text: 'Blog', href: getBlogCanonicalBaseUrl() },
                { text: 'FAQ', href: getNavigationUrl('/faq') },
                { text: 'Travel & stays', href: getNavigationUrl('/travel') },
                ...(isPerDiemEstimatorEnabled()
                    ? [{ text: 'Per diem calculator', href: getNavigationUrl('/travel/per-diem') }]
                    : []),
                { text: 'TICS', href: getNavigationUrl('/standards/tics') },
                { text: 'Support', href: getNavigationUrl('/support') },
                { text: 'Documentation', href: getNavigationUrl('/docs') },
                { text: 'TX-RAMP', href: getNavigationUrl('/tx-ramp-compliance') },
            ],
        }
    ], []);
    const infoMailbox = siteContact?.infoEmail ?? 'info@americanpolicetraining.com';
    const displayPhone = React.useMemo(() => {
        if (siteContact && !siteContact.mainPhoneVisible) {
            return null;
        }
        if (siteContact?.mainPhoneVisible) {
            return primaryPhone ?? resolvePublicMainPhone(siteContact);
        }
        return null;
    }, [primaryPhone, siteContact]);
    const displayAddress = React.useMemo(
        () => resolvePublicMainAddress(siteContact, primaryAddress),
        [primaryAddress, siteContact],
    );
    const displayEmail = primaryEmail ?? siteContact?.infoEmail ?? null;
    const socialLinks = React.useMemo((): SocialLink[] => [
        { icon: <Facebook />, href: 'https://facebook.com', label: 'Facebook' },
        { icon: <Twitter />, href: 'https://twitter.com', label: 'Twitter' },
        { icon: <LinkedIn />, href: 'https://linkedin.com', label: 'LinkedIn' },
        { icon: <Email />, href: `mailto:${infoMailbox}`, label: 'Email' },
    ], [infoMailbox]);

    if (scorecardFooter) {
        return (
            <Box component="footer" role="contentinfo" aria-label="Site footer" sx={{
                bgcolor: 'background.paper',
                borderTop: 1,
                borderColor: 'divider',
                mt: 'auto',
                flexShrink: 0,
            }}>
                <Box sx={{
                    width: '100%',
                    minWidth: 0,
                    py: 1.5,
                    px: AGENCY_SHELL_HORIZONTAL_PX,
                }}>
                    <Typography variant="body2" color="text.secondary">
                        © {currentYear} American Police Training ·{' '}
                        <Link href={getNavigationUrl('/privacy')} color="inherit" underline="hover">Privacy</Link>
                        {' · '}
                        <Link href={getNavigationUrl('/terms')} color="inherit" underline="hover">Terms</Link>
                    </Typography>
                </Box>
            </Box>
        );
    }

    if (appShellFooter) {
        return (
            <Box component="footer" role="contentinfo" aria-label="Site footer" sx={{
                bgcolor: 'background.paper',
                borderTop: 1,
                borderColor: 'divider',
                mt: 'auto',
                flexShrink: 0,
            }}>
                <Box sx={{
                    width: '100%',
                    minWidth: 0,
                    py: 1.5,
                    px: AGENCY_SHELL_HORIZONTAL_PX,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 1,
                }}>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            American Police Training
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            © {currentYear} ·{' '}
                            <Link href={getNavigationUrl('/privacy')} color="inherit" underline="hover">Privacy</Link>
                            {' · '}
                            <Link href={getNavigationUrl('/terms')} color="inherit" underline="hover">Terms</Link>
                            {' · '}
                            <Link href={getNavigationUrl('/support')} color="inherit" underline="hover">Support</Link>
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Security Status
                        </Typography>
                        <SecurityStatusWidget compact/>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (<Box component="footer" role="contentinfo" aria-label="Site footer" sx={{
            bgcolor: 'background.paper',
            borderTop: 1,
            borderColor: 'divider',
            mt: 'auto',
            flexShrink: 0,
        }}>
      <Box sx={{
            width: '100%',
            minWidth: 0,
            py: denseSettingsFooter
                ? { xs: 2.5, sm: 3, md: 3.5 }
                : { xs: 4, sm: 5, md: 6 },
            px: AGENCY_SHELL_HORIZONTAL_PX,
        }}>
        {/* Company Info and Footer Links Container */}
        <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', lg: 'row' },
            gap: denseSettingsFooter
                ? { xs: 2, sm: 2.5, md: 3 }
                : { xs: 3, sm: 4, md: 5 },
            mb: denseSettingsFooter
                ? { xs: 1.5, sm: 2, md: 2.5 }
                : { xs: 2, sm: 3, md: 4 }
        }}>
          {/* Company Info */}
          <Box sx={{
            flex: { xs: '1 1 100%', lg: '0 0 33.333%' },
            minWidth: 0
        }}>
            <Box
              sx={{
                mb: denseSettingsFooter ? { xs: 1.25, sm: 1.75 } : { xs: 2, sm: 3 },
                // Icon mark below md; full lockup from md. Keep square width on xs/sm
                // so the mark is not letterboxed inside a full-logo reservation.
                height: denseSettingsFooter
                    ? { xs: 32, sm: 40, md: 48 }
                    : { xs: 40, sm: 48, md: 56 },
                width: denseSettingsFooter
                    ? { xs: 32, sm: 40, md: 280 }
                    : { xs: 40, sm: 48, md: 364 },
                maxWidth: '100%',
                flexShrink: 0,
              }}
            >
              <ResponsiveLogo
                mobileVariant="icon"
                tabletVariant="full"
                desktopVariant="full"
                mobileSize="small"
                tabletSize="medium"
                desktopSize="large"
                transparent={true}
                wide={false}
                noBuffer={true}
                sx={{
                  height: '100%',
                  width: '100%',
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain',
                  objectPosition: 'left center',
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" paragraph sx={{
            fontWeight: 400,
            mb: denseSettingsFooter ? { xs: 1.25, sm: 1.75 } : { xs: 2, sm: 3 },
            lineHeight: 1.5,
            minHeight: denseSettingsFooter ? '2.2em' : '3.2em',
        }}>
              {settings.footer_description || 'Empowering law enforcement professionals with comprehensive training solutions and modern event management tools.'}
            </Typography>
            
            {/* Contact Info — reserved height so async phone/email/address do not expand footer */}
            <Box sx={{
            mt: denseSettingsFooter ? { xs: 1.25, sm: 1.75 } : { xs: 2, sm: 3 },
            minHeight: denseSettingsFooter ? 56 : 72,
            '& > *:not(:last-child)': {
                mb: denseSettingsFooter ? { xs: 1, sm: 1.25 } : { xs: 1.5, sm: 2 }
            }
        }}>
              {displayPhone && (<Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Phone sx={{ fontSize: 16, mr: 1.5, color: 'text.secondary' }}/>
                  <Typography variant="body2" color="text.secondary">
                    {displayPhone}
                  </Typography>
                </Box>)}
              {displayEmail && (<Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Email sx={{ fontSize: 16, mr: 1.5, color: 'text.secondary' }}/>
                  <Typography variant="body2" color="text.secondary">
                    {displayEmail}
                  </Typography>
                </Box>)}
              {displayAddress && (<Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOn sx={{ fontSize: 16, mr: 1.5, color: 'text.secondary' }}/>
                  <Typography variant="body2" color="text.secondary">
                    {displayAddress}
                  </Typography>
                </Box>)}
            </Box>
          </Box>

          {/* Footer Links — accordion on mobile, grid on sm+ */}
          {isMobile ? (
            <Box sx={{ flex: '1 1 100%', minWidth: 0, width: '100%' }}>
              {footerSections.map((section) => (
                <Accordion
                  key={section.title}
                  disableGutters
                  elevation={0}
                  sx={{
                    bgcolor: 'transparent',
                    '&:before': { display: 'none' },
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {section.title}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <Box
                      component="ul"
                      role="list"
                      aria-label={`${section.title} links`}
                      sx={{ listStyle: 'none', p: 0, m: 0 }}
                    >
                      {section.links.map((link) => (
                        <Box component="li" key={link.text} sx={{ mb: 1 }}>
                          <Link
                            href={link.href}
                            onClick={link.onClick}
                            rel={isRobotsDisallowedPath(link.href) ? 'nofollow' : undefined}
                            color="text.secondary"
                            aria-label={link.text}
                            sx={{
                              textDecoration: 'none',
                              fontSize: '0.875rem',
                              '&:hover': { color: 'primary.main' },
                            }}
                          >
                            {link.text}
                          </Link>
                        </Box>
                      ))}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ) : (
          <Grid container spacing={{ sm: 2, md: 3, lg: 4 }} sx={{
            flex: { xs: '1 1 100%', lg: '0 0 66.667%' },
            minWidth: 0
        }}>
            {footerSections.map((section) => (<Grid item xs={6} sm={4} md={2} lg={2} key={section.title}>
              <Typography variant="h6" gutterBottom sx={{
                color: 'text.primary',
                fontWeight: 700,
                mb: { sm: 1.5, md: 2 },
                fontSize: { sm: '1rem', md: '1.1rem' }
            }}>
                {section.title}
              </Typography>
              <Box component="ul" role="list" aria-label={`${section.title} links`} sx={{
                listStyle: 'none',
                p: 0,
                m: 0,
                '& > *:not(:last-child)': {
                    mb: { sm: 1, md: 1.25 }
                }
            }}>
                {section.links.map((link) => (<Box component="li" key={link.text}>
                    <Link href={link.href} onClick={link.onClick} rel={isRobotsDisallowedPath(link.href) ? 'nofollow' : undefined} color="text.secondary" aria-label={link.text} sx={{
                    textDecoration: 'none',
                    fontSize: { sm: '0.875rem', md: '0.9rem' },
                    '&:hover': {
                        color: 'primary.main',
                    },
                    '&:focus': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: '2px',
                    },
                }}>
                      {link.text}
                    </Link>
                  </Box>))}
              </Box>
            </Grid>))}
          </Grid>
          )}
        </Box>

        <Divider sx={{
            my: { xs: 2, sm: 3, md: 4 },
            opacity: 0.6
        }}/>

        {/* Bottom Section */}
        <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: { xs: 2, sm: 3 },
            textAlign: { xs: 'center', sm: 'left' }
        }}>
          {/* Copyright + trademark attribution */}
          <Box sx={{
            order: { xs: 3, sm: 1 },
            width: { xs: '100%', sm: 'auto' },
            maxWidth: { sm: '52%' },
            textAlign: { xs: 'center', sm: 'left' },
          }}>
            <Typography variant="body2" color="text.secondary">
              {(settings.footer_copyright_text || `© ${currentYear} American Police Training. All rights reserved.`).replace('{year}', currentYear.toString())}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.5 }}>
              {TICS_TRADEMARK_ATTRIBUTION}
            </Typography>
          </Box>

          {/* Security Status */}
          <Box sx={{
            order: { xs: 1, sm: 1 },
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            justifyContent: { xs: 'center', sm: 'flex-start' },
            width: { xs: '100%', sm: 'auto' },
            marginRight: { sm: 'auto' }
        }}>
            <Typography variant="body2" color="text.secondary">
              Security Status:
            </Typography>
            <SecurityStatusWidget compact/>
          </Box>

          {/* Social Links */}
          {settings.footer_show_social_links && (<Box sx={{
                display: 'flex',
                gap: { xs: 1.5, sm: 2 },
                order: { xs: 2, sm: 3 },
                justifyContent: { xs: 'center', sm: 'flex-end' },
                width: { xs: '100%', sm: 'auto' }
            }}>
              {socialLinks.map((social) => (<Link key={social.label} href={social.href} target="_blank" rel="noopener noreferrer" sx={{
                    color: 'text.secondary',
                    p: 0.5,
                    borderRadius: 1,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                        color: 'primary.main',
                        bgcolor: 'action.hover',
                        transform: 'translateY(-1px)',
                    },
                    '&:focus': {
                        outline: '2px solid',
                        outlineColor: 'primary.main',
                        outlineOffset: '2px',
                    },
                }} aria-label={`Follow us on ${social.label}`}>
                  {social.icon}
                  <VisuallyHiddenText>Follow us on {social.label}</VisuallyHiddenText>
                </Link>))}
            </Box>)}
        </Box>
      </Box>
    </Box>);
};
export default Footer;
